import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FileTextIcon } from '@/components/ui/icons';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import {
  BloggerIcon,
  ConferencierIcon,
  AgronomeIcon,
} from '@/components/contact/ContactIcons';
import { useTranslation } from 'react-i18next';

export function AboutCorentinSection() {
  const { t } = useTranslation('contact');

  const roles = [
    {
      icon: BloggerIcon,
      color: '#2563EB',
      bgColor: 'bg-blue-50',
      title: t('about.roles.blogger.title'),
      description: t('about.roles.blogger.description'),
    },
    {
      icon: ConferencierIcon,
      color: '#9333EA',
      bgColor: 'bg-purple-50',
      title: t('about.roles.speaker.title'),
      description: t('about.roles.speaker.description'),
    },
    {
      icon: AgronomeIcon,
      color: '#55BC8C',
      bgColor: 'bg-aspexit-green/10',
      title: t('about.roles.agronomist.title'),
      description: t('about.roles.agronomist.description'),
    },
  ];

  const stats = [
    { number: '60+', label: t('about.stats.articles') },
    { number: '10+', label: t('about.stats.experience') },
    { number: '100+', label: t('about.stats.projects') },
    { badge: true, label: t('about.stats.qualiopi'), sublabel: t('about.stats.qualiopiSublabel') },
  ];
  return (
    <section className="py-12 bg-aspexit-gray-light">
      <div className="container mx-auto px-4">
        <Card className="max-w-5xl mx-auto p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-12">
            {/* Photo + Stats */}
            <div className="flex flex-col items-center gap-8">
              {/* Photo réduite */}
              <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-white shadow-xl flex-shrink-0">
                <OptimizedImage
                  src="/corentin-leroux.jpg"
                  alt="Corentin Leroux - Expert en Numérique Agricole"
                  fallbackSrc="/corentin-leroux.jpg"
                  aspectRatio="1/1"
                  loading="eager"
                />
              </div>

              {/* Stats sous la photo - Cards alignées */}
              <div className="grid grid-cols-1 gap-3 w-full">
                {stats.map((stat, index) => (
                  <Card key={index} className="p-4 bg-white border border-border hover:shadow-md transition-shadow">
                    {stat.badge ? (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-aspexit-green/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-base text-aspexit-green font-bold">✓</span>
                        </div>
                        <div>
                          <div className="text-sm font-heading font-semibold text-aspexit-black">
                            {stat.label}
                          </div>
                          <div className="text-xs text-aspexit-gray">
                            {stat.sublabel}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="text-2xl font-heading font-bold text-aspexit-green flex-shrink-0">
                          {stat.number}
                        </div>
                        <div className="text-xs text-aspexit-gray">
                          {stat.label}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl font-heading font-bold text-aspexit-black mb-2">
                {t('about.name')}
              </h1>
              <p className="text-xl text-aspexit-gray mb-8">
                {t('about.tagline')}
              </p>

              {/* Roles */}
              <div className="space-y-6 mb-8">
                {roles.map((role) => {
                  const IconComponent = role.icon;
                  return (
                    <div
                      key={role.title}
                      className="flex items-start gap-4 text-left"
                    >
                      <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${role.bgColor} flex items-center justify-center`}>
                        <IconComponent className="w-6 h-6" color={role.color} />
                      </div>
                      <div>
                        <h2 className="text-lg font-heading font-semibold text-aspexit-black mb-1">
                          {role.title}
                        </h2>
                        <p className="text-aspexit-gray">{role.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bio */}
              <div className="bg-white p-6 rounded-lg border-l-4 border-aspexit-green mb-8">
                <p className="text-aspexit-black leading-relaxed">
                  {t('about.bio')}
                </p>
              </div>

              {/* Links */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Button
                  asChild
                  variant="outline"
                  className="border-aspexit-green text-aspexit-green hover:bg-aspexit-green hover:text-white"
                >
                  <a
                    href="https://www.linkedin.com/in/corentin-leroux-0225a182/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    {t('about.links.linkedin')}
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <a
                    href="https://www.aspexit.com/wp-content/uploads/2025/11/CV_Corentin_Leroux_2026_FR.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    <FileTextIcon className="w-4 h-4" size={16} />
                    {t('about.links.downloadCV')}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}
